import React from 'react';
import CandidateHistory from '@/components/candidates/CandidateHistory';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import candidateService from '@/services/candidateService';
import Loader from '@/components/Loader';
import UniversityRepeatableField from '@/components/Frontend/components/UniversityRepeatableField';

const useCandidateDetails = () => {
  const { candidate_id } = useParams();
  const [candidate, setCandidate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCandidate = async () => {
      try {
        const data = await candidateService.getCandidateById(candidate_id);
        setCandidate(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCandidate();
  }, [candidate_id]);

  return { candidate, isLoading, error };
};

const CandidateDetails = () => {

  const navigate = useNavigate();
  const { candidate, isLoading, error } = useCandidateDetails();
  const [showCvPreview, setShowCvPreview] = useState(false);

  if (isLoading) {
    return (
      <div className="flex flex-col min-h-screen items-center justify-center bg-card">
        <Loader bgColor="white" loadingText="Loading candidate profile..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col min-h-screen items-center justify-center bg-background">
        <div className="text-red-500 font-medium font-['Inter']">Error: {error}</div>
      </div>
    );
  }

  if (!candidate) {
    return (
      <div className="flex flex-col min-h-screen items-center justify-center bg-background">
        <div className="text-muted-foreground font-medium font-['Inter']">Candidate not found</div>
      </div>
    );
  }

  const renderFieldValue = (field) => {
    // Handle university_repeatable field type
    if (field.field_type === "university_repeatable") {
      return (
        <UniversityRepeatableField
          fieldIndex={0}
          field={{ label: field.field_name }}
          isRequired={false}
          displayMode={true}
          initialValue={field.field_value}
        />
      );
    }

    // Handle other field types
    return (
      <p className="font-medium bg-muted mt-1.5 rounded-lg p-3 text-foreground w-full border border-border leading-6 font-['Inter']">
        {field.field_value || 'N/A'}
      </p>
    );
  };

  const applications = Array.isArray(candidate?.data?.applications)
    ? [...candidate.data.applications]
    : [];

  const latestApplicationWithResume = applications
    .sort((a, b) => {
      const aTime = new Date(a?.created_at || 0).getTime();
      const bTime = new Date(b?.created_at || 0).getTime();
      return bTime - aTime;
    })
    .find((application) => application?.resume);

  const latestResumeUrl = latestApplicationWithResume?.resume || "";

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <div className="flex-1 w-[90%] pt-12 pb-24 px-8 mx-auto flex flex-col gap-10">
        {/* Header Section */}
        <div className="flex flex-col gap-4">
          <div className="flex justify-between items-start flex-wrap gap-4">
            <div className="flex flex-col gap-1">
              <h1 className="text-foreground text-2xl font-semibold font-['Inter'] leading-8">
                Candidate Profile
              </h1>
              <p className="text-muted-foreground text-base font-normal font-['Inter'] leading-6">
                Detailed view of the candidate's professional background, applications, and historical interactions.
              </p>
            </div>
            <button
               onClick={() => navigate(-1)}
               className="py-2 px-4 rounded-lg text-white bg-primary-dark border border-input bg-dark   flex items-center   cursor-pointer "
            >
               Back
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-card p-6 rounded-xl shadow-sm border border-border flex flex-col gap-6">
            <h2 className="text-lg font-semibold text-foreground font-['Inter']">Personal Information</h2>
            <div className="space-y-5">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Full Name</label>
                <input
                  type="text"
                  value={candidate?.data?.candidate?.name || 'N/A'}
                  disabled
                  className="w-full px-4 py-3 border border-border rounded-lg bg-muted text-foreground font-medium font-['Inter']"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Email Address</label>
                <input
                  type="email"
                  value={candidate?.data?.candidate?.email || 'N/A'}
                  disabled
                  className="w-full px-4 py-3 border border-border rounded-lg bg-muted text-foreground font-medium font-['Inter']"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <button
                  className="py-2 px-4 rounded-lg bg-primary-dark text-white font-semibold cursor-pointer transition-all duration-200 w-fit"
                  onClick={() => {
                    console.log("Latest application resume:", latestResumeUrl);
                    setShowCvPreview((prev) => !prev);
                  }}
                >
                  {showCvPreview ? "Hide CV" : "Show CV"}
                </button>
                {showCvPreview && (
                  <div className="mt-2 rounded-lg border border-border overflow-hidden h-[90vh] bg-card">
                    {latestResumeUrl ? (
                      <iframe
                        title="Candidate Resume Preview"
                        src={`${latestResumeUrl}`}
                        className="w-full h-full border-0"
                        scrolling="no"
                      />
                    ) : (
                      <div className="p-4 text-sm text-muted-foreground bg-muted">
                        Latest application resume is not available.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-card p-6 rounded-xl shadow-sm border border-border flex flex-col gap-6">
            <h2 className="text-lg font-semibold text-foreground font-['Inter']">Latest Application Data</h2>
            {candidate?.data?.latest_application ? (
              <div className="space-y-6">
                {candidate.data.latest_application.application_fields.map((field) => (
                  <div key={field.id} className="flex flex-col gap-1.5">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{field.field_name}</p>
                    {renderFieldValue(field)}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center py-10 bg-muted rounded-lg border border-dashed border-border">
                 <p className="text-muted-foreground text-sm italic font-['Inter']">No application data available</p>
              </div>
            )}
          </div>
        </div>

        <div className="mt-2">
          <CandidateHistory candidate={candidate} />
        </div>
      </div>
    </div>
  );
};

export default CandidateDetails;
